import { useCallback, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from './supabase.js'
import { isFeatureEnabled } from './featureFlags'
import { permissionCache } from './permissionCache.js'
import { useSupabaseAuth } from './useSupabaseAuth'
import { CACHE_TIMES } from './query-client'

// Single React Query-backed source of truth for the current user's role and
// tab access. Every consumer (~12 across the app) used to fire its own
// supabase fetch + custom in-memory cache + realtime channel; this hook
// dedupes everything behind one query key.
//
// Key: ['userAccessCache', userId]
//
// Realtime invalidation is wired in ReactQueryDataProvider (single channel,
// filtered by user_id) so the hook stays purely declarative.
//
// The shared `permissionCache` module is still kept in sync as a write-only
// side effect of the queryFn: `getCompanyId()` in @prosperia/shared reads
// from it as a fast path during boot, and we don't want to regress that.

export const userAccessCacheKey = (userId) => ['userAccessCache', userId]

const OWNER_ALL_TABS = () => ({
  dashboard: true,
  ...(isFeatureEnabled('TOOLBELT_ENABLED') ? { toolbelt: true } : {}),
  'ai-assistant': true,
  clients: true,
  jobs: true,
  estimates: true,
  services: true,
  invoices: true,
  expenses: true,
  finances: true,
  employees: true,
  scheduling: true,
  communications: true,
  reports: true,
  files: true,
  automations: true,
  ...(isFeatureEnabled('INSPECTION_ENABLED') ? { inspection: true } : {}),
  'company-branding': true,
  reviews: true,
  items: true,
  'expenses:read': true,
  'expenses:write': true,
  'expenses:manage': true,
})

const ENUM_TO_TAB = {
  dashboard: 'dashboard',
  clients: 'clients',
  jobs: 'jobs',
  estimates: 'estimates',
  invoices: 'invoices',
  payments: 'payments',
  expenses: 'expenses',
  employees: 'employees',
  reports: 'reports',
  settings: 'company-branding',
  toolbelt: 'toolbelt',
  'ai-assistant': 'ai-assistant',
}

const convertEnumPermissionsToTabs = (enumPermissions) => {
  if (!Array.isArray(enumPermissions)) return {}
  if (enumPermissions.includes('all')) return OWNER_ALL_TABS()

  const tabPermissions = {}
  enumPermissions.forEach((permission) => {
    const tabName = ENUM_TO_TAB[permission]
    if (tabName) tabPermissions[tabName] = true
  })
  return tabPermissions
}

const fetchUserAccess = async (userId) => {
  const { data, error } = await supabase
    .from('user_access_cache')
    .select('company_id, role_type, permissions, is_owner, is_active')
    .eq('user_id', userId)
    .eq('is_active', true)
    .maybeSingle()

  if (error) {
    // PGRST116 = no row; treat as "user has no active access" rather than
    // an error so the UI can render an empty/locked state instead of a
    // perpetual loading spinner.
    if (error.code === 'PGRST116') return null
    throw error
  }
  return data
}

export const usePermissions = () => {
  const { user, authReady, isAuthenticated } = useSupabaseAuth()
  const queryClient = useQueryClient()
  const userId = user?.id ?? null

  const query = useQuery({
    queryKey: userAccessCacheKey(userId),
    queryFn: async () => {
      const accessData = await fetchUserAccess(userId)
      // Keep the shared permissionCache warm for getCompanyId()'s fast path.
      if (accessData) {
        permissionCache.setCachedPermissions(
          accessData.permissions,
          accessData.company_id,
          userId,
          accessData.role_type,
          accessData.is_owner,
        )
      }
      return accessData
    },
    enabled: !!userId && authReady && isAuthenticated,
    staleTime: CACHE_TIMES.ESSENTIAL_STALE,
    gcTime: CACHE_TIMES.GC_TIME,
    retry: 1,
  })

  const accessData = query.data

  const { permissions, isOwner, isEmployee, companyId } = useMemo(() => {
    if (!accessData) {
      return { permissions: {}, isOwner: false, isEmployee: false, companyId: null }
    }

    const { company_id, role_type, permissions: userPermissions, is_owner, is_active } = accessData

    if (is_owner) {
      return {
        permissions: OWNER_ALL_TABS(),
        isOwner: true,
        isEmployee: false,
        companyId: company_id,
      }
    }

    if (role_type === 'employee' && is_active && userPermissions) {
      const tabPermissions = convertEnumPermissionsToTabs(userPermissions)
      // Employees always get settings access; can't be revoked by owners.
      tabPermissions.settings = true
      return {
        permissions: tabPermissions,
        isOwner: false,
        isEmployee: true,
        companyId: company_id,
      }
    }

    return {
      permissions: {},
      isOwner: false,
      isEmployee: role_type === 'employee',
      companyId: company_id,
    }
  }, [accessData])

  const getAccessibleTabNames = useCallback(() => {
    if (isOwner) {
      const baseTabs = [
        'dashboard', 'clients', 'jobs', 'estimates', 'services', 'invoices', 'expenses',
        'finances', 'employees', 'scheduling', 'communications', 'reports', 'files',
        'automations', 'ai-assistant',
      ]
      if (isFeatureEnabled('INSPECTION_ENABLED')) baseTabs.push('inspection')
      if (isFeatureEnabled('TOOLBELT_ENABLED')) baseTabs.push('toolbelt')
      return baseTabs
    }

    return Object.entries(permissions)
      .filter(([, hasAccess]) => hasAccess === true)
      .map(([tabName]) => tabName)
  }, [permissions, isOwner])

  const canReadExpenses = useCallback(() => {
    if (isOwner) return true
    return permissions['expenses:read'] === true || permissions.expenses === true
  }, [permissions, isOwner])

  const refreshPermissions = useCallback(() => {
    permissionCache.clearCache()
    queryClient.invalidateQueries({ queryKey: userAccessCacheKey(userId) })
  }, [queryClient, userId])

  return {
    permissions,
    accessibleTabs: permissions,
    isOwner,
    isEmployee,
    companyId,
    loading: query.isLoading,
    error: query.error ? query.error.message : null,
    getAccessibleTabNames,
    canReadExpenses,
    refreshPermissions,
  }
}

export default usePermissions
